'use client';

import { useCallback, useEffect, useMemo } from 'react';
import useOrderbookDisplayStore from '../../stores/orderbook/useOrderbookDisplayStore';
import OrderbookDisplay from './OrderbookDisplay';
import { OrderbookGroupingOptions } from './OrderbookTypes';
import useDriftStore from '../../stores/DriftStore/useDriftStore';
import {
	MarketId,
	OrderbookGrouping,
	MarketPriceState,
} from '@velocity-exchange/common';
import { BigNum, PRICE_PRECISION_EXP } from '@velocity-exchange/sdk';
import useGetTickSizeForMarket from '../../hooks/useGetTickSizeForMarket';
import { ORDERBOOK_GROUPING_OPTIONS } from './OrderbookConstants';
import { useOrderbookGroupingPreferences } from '../../hooks/useOrderbookGroupingPreferences';
import { useMarketPriceState } from 'src/hooks/query/orderbook/useMarketPriceState';

const useGroupingSizeOptions = (
	selectedMarketId: MarketId | null
): OrderbookGroupingOptions => {
	const getTickSizeForMarket = useGetTickSizeForMarket();

	const tickSizeBigNum = useMemo(
		() =>
			selectedMarketId
				? BigNum.from(
						getTickSizeForMarket(selectedMarketId),
						PRICE_PRECISION_EXP
					)
				: BigNum.from(1, PRICE_PRECISION_EXP),
		[getTickSizeForMarket, selectedMarketId?.key]
	);

	const groupingSizeOptions = useMemo(
		() =>
			ORDERBOOK_GROUPING_OPTIONS.map((opt) => {
				return {
					quoteVal: BigNum.from(opt).mul(tickSizeBigNum).toNum(),
					groupingSize: opt,
				};
			}),
		[tickSizeBigNum]
	);

	return groupingSizeOptions;
};

type OrderbookProps = {
	fixedVerticalSideDepth?: number;
	fixedHorizontalSideDepth?: number;
	isMini?: boolean;
	dataTestId?: string;
};

const Orderbook = (props: OrderbookProps) => {
	const orderbookState = useOrderbookDisplayStore();

	const latestMarketTrades = useDriftStore((s) => s.marketTradeHistory.trades);

	const { data: marketPriceState } = useMarketPriceState(
		orderbookState.marketId
	);

	const { getGroupingSize, setGroupingSize } = useOrderbookGroupingPreferences(
		orderbookState.marketId?.key ?? ''
	);

	useEffect(() => {
		orderbookState.set((s) => {
			s.isDisplayingOrderbook = true;
		});

		return () => {
			orderbookState.set((s) => {
				s.isDisplayingOrderbook = false;
			});
		};
	}, []);

	useEffect(() => {
		if (orderbookState.marketId) {
			const savedGrouping = getGroupingSize(
				orderbookState.groupingSizeSelection
			);
			if (savedGrouping !== orderbookState.groupingSizeSelection) {
				orderbookState.setGroupingSizeSelection(savedGrouping);
			}
		}
	}, [
		orderbookState.marketId,
		getGroupingSize,
		orderbookState.groupingSizeSelection,
	]);

	const groupingSizeOptions = useGroupingSizeOptions(orderbookState.marketId);

	const updateGroupingSizeSelection = useCallback(
		(grouping: OrderbookGrouping) => {
			orderbookState.setGroupingSizeSelection(grouping);
			setGroupingSize(grouping);
		},
		[setGroupingSize]
	);

	return (
		<OrderbookDisplay
			marketId={orderbookState.marketId as MarketId}
			orderbookDisplayState={orderbookState.orderbookDisplayState}
			groupingSizeOptions={groupingSizeOptions}
			groupingSizeSelection={orderbookState.groupingSizeSelection}
			currentUserBidsAndAsks={
				orderbookState.currentUserBidsAndAsks ?? { bids: [], asks: [] }
			}
			loading={orderbookState.loading}
			marketPriceState={marketPriceState as MarketPriceState}
			latestMarketTrades={latestMarketTrades}
			isDisplayingOrderbook={orderbookState.isDisplayingOrderbook}
			updateGroupingSizeSelection={updateGroupingSizeSelection}
			{...props}
		/>
	);
};

export default Orderbook;
